/**
 * src/SecurityPage.js
 *
 * VISINVOKE Security landing page.
 * A new top-level product family. Amber / gold section identity, standard
 * VISINVOKE header, and two large flagship product cards built in the cleaner
 * Windows Knowledge Centre card language (scaled up): Managed PC Protection
 * (deep green) and Track, Lock & Wipe (deep blue).
 *
 * Each card has a single primary action that opens the user's default email
 * client (mailto) with a pre-filled subject, so they can contact VISINVOKE to
 * enquire about the service. No external website links.
 */
import React, { useEffect } from 'react';
import { recordProductOpened, recordProductOperation } from './productActivity';
import ProductNav from './components/ProductNav';
import MoreInfoPill from './components/MoreInfoPill';
import { PRODUCTS } from './config/products';
import { glassSurface } from './config/glassTheme';

// Contact address for service enquiries.
const ENQUIRY_EMAIL = 'support@visinvoke.com';

// Fallback so the header badge still renders if the Security theme has not yet
// been added to config/products.js.
const SEC = PRODUCTS['security'] || {
  icon: '\uD83D\uDEE1\uFE0F',
  displayName: 'Security',
  accentGradient: 'linear-gradient(135deg, #CA8A0C 0%, #F2B91E 55%, #FFD447 100%)',
};

const SEC_MOTION_CSS = `
  .sec-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 460ms cubic-bezier(0.22,1,0.36,1),
                transform 460ms cubic-bezier(0.22,1,0.36,1);
  }
  .sec-reveal.sec-in { opacity: 1; transform: translateY(0); }
  .sec-reveal-delay-1 { transition-delay: 70ms; }
  .sec-reveal-delay-2 { transition-delay: 140ms; }
  .sec-card {
    transition: transform 240ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 240ms ease, border-color 240ms ease !important;
  }
  @media (max-width: 820px) {
    .sec-info-grid { grid-template-columns: 1fr !important; }
    .sec-card-grid { grid-template-columns: 1fr !important; }
  }
`;

// -- Service definitions ------------------------------------------------------

// Canonical VISINVOKE product keys for the two managed services on this page. Declared here so the
// enquiry event names the PRODUCT, not the page - the Security product line is a third, separate
// product that this page also represents.
const SECURITY_PRODUCT_KEYS = {
  'managed-pc-protection': 'security_managed_pc_protection',
  'track-lock-wipe':       'security_track_lock_wipe',
};

const SERVICES = [
  {
    key: 'managed-pc-protection',
    eyebrow: 'Managed Protection',
    name: 'Managed PC Protection',
    icon: '🛡️',
    purpose: 'Professional managed computer protection that VISINVOKE manages and continuously monitors to help keep your computer protected.',
    accent: '#16a34a',
    accent2: '#15803d',
    glow: 'rgba(22,163,74,0.22)',
    bright: '#4ade80',
    headGrad: 'linear-gradient(155deg, #0a1512 0%, #123024 52%, #0d4230 100%)',
    features: [
      'Professional-grade protection',
      'Professionally managed',
      'Continuous monitoring',
      'Email support included',
    ],
    price: '$9.99',
    ctaLabel: 'Get Protection',
    enquirySubject: 'Managed PC Protection - Enquiry',
    enquiryBody: `Hello VISINVOKE,

I would like to get Managed PC Protection for my computer(s).

Number of Computers:

Country:

Additional Information:

Thank you.
`,
  },
  {
    key: 'track-lock-wipe',
    eyebrow: 'Loss & Theft Protection',
    name: 'Track, Lock & Wipe',
    icon: '🔒',
    purpose: 'Protect your computer and its information if it is ever lost or stolen.',
    accent: '#2563eb',
    accent2: '#1d4ed8',
    glow: 'rgba(37,99,235,0.22)',
    bright: '#60a5fa',
    headGrad: 'linear-gradient(155deg, #0a1224 0%, #16264f 52%, #1c3aa0 100%)',
    features: [
      'Track missing devices',
      'Remote lock',
      'Remote wipe',
      'Location assistance',
      'Data protection',
      'Privacy protection',
    ],
    price: '$9.99',
    ctaLabel: 'Get Protection',
    enquirySubject: 'Track, Lock & Wipe - Enquiry',
    enquiryBody: `Hello VISINVOKE,

I would like to get the Track, Lock & Wipe service for my computer(s).

Number of Computers:

Country:

Additional Information:

Thank you.
`,
  },
];

// Lower "How VISINVOKE Security Works" overview columns. Concise, current model:
// managed + monitored protection, loss/theft protection, and self-service ongoing management.
const INFO_COLUMNS = [
  {
    icon: '🛡️',
    label: 'Managed Protection',
    body: 'VISINVOKE provides ongoing managed PC protection designed to help keep your supported computers protected and continuously monitored.',
    accentText: '#86efac',
    line: 'linear-gradient(90deg, transparent, #16a34a, transparent)',
    bg: 'linear-gradient(180deg, rgba(22,163,74,0.10) 0%, rgba(22,163,74,0.02) 100%)',
    border: 'rgba(22,163,74,0.30)',
    iconBg: 'rgba(22,163,74,0.14)',
    iconBorder: 'rgba(22,163,74,0.45)',
  },
  {
    icon: '🔒',
    label: 'Loss & Theft Protection',
    body: 'Track, Lock & Wipe helps protect your computer and its information if the device is ever lost or stolen.',
    accentText: '#93c5fd',
    line: 'linear-gradient(90deg, transparent, #2563eb, transparent)',
    bg: 'linear-gradient(180deg, rgba(37,99,235,0.10) 0%, rgba(37,99,235,0.02) 100%)',
    border: 'rgba(37,99,235,0.30)',
    iconBg: 'rgba(37,99,235,0.14)',
    iconBorder: 'rgba(37,99,235,0.45)',
  },
  {
    icon: '⚙️',
    label: 'Simple Ongoing Service',
    body: 'Choose the protection you need and manage it from your VISINVOKE account, kept connected to the same VISINVOKE ecosystem as the rest of your products.',
    accentText: '#FFDE7A',
    line: 'linear-gradient(90deg, transparent, #F2B91E, transparent)',
    bg: 'linear-gradient(180deg, rgba(242,185,30,0.10) 0%, rgba(242,185,30,0.02) 100%)',
    border: 'rgba(242,185,30,0.30)',
    iconBg: 'rgba(242,185,30,0.14)',
    iconBorder: 'rgba(242,185,30,0.45)',
  },
];

// -- Flagship card ------------------------------------------------------------

function ServiceCard({ svc, delay }) {
  return (
    <div
      className={`sec-card sec-reveal ${delay}`}
      style={{ ...pg.card, borderColor: `${svc.accent}40` }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 28px 70px rgba(0,0,0,0.5), 0 0 0 1px ${svc.accent}55`; e.currentTarget.style.borderColor = `${svc.accent}80`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 50px rgba(0,0,0,0.4)'; e.currentTarget.style.borderColor = `${svc.accent}40`; }}
    >
      {/* Header band */}
      <div style={{ ...pg.cardHead, background: svc.headGrad }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 22% 30%, ${svc.glow} 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${svc.accent}, transparent)` }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18, zIndex: 1 }}>
          <div style={{ ...pg.iconBox, background: `${svc.accent}26`, border: `1px solid ${svc.accent}66` }}>{svc.icon}</div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: svc.accent === '#16a34a' ? '#86efac' : '#93c5fd' }}>{svc.eyebrow}</div>
            <h2 style={pg.cardTitle}>{svc.name}</h2>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={pg.cardBody}>
        <p style={pg.purpose}>{svc.purpose}</p>

        <div style={pg.featGrid}>
          {svc.features.map((f, i) => (
            <div key={i} style={pg.feat}>
              <span style={{ ...pg.featMark, color: svc.accent }}>{'\u2713'}</span>
              <span>{f}</span>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div style={pg.priceBlock}>
          <div style={pg.priceMain}>
            <span style={pg.priceNum}>{svc.price}</span>
            <span style={pg.pricePer}>USD / month per PC</span>
          </div>
        </div>

        {/* CTA - opens the user's email client with a pre-filled enquiry.
            These are enquiry-driven managed services: there is no agent, no device record and no
            service execution on this platform, so pressing THIS service's enquiry button is the
            only real, product-specific customer action there is. A page open would be a fabricated
            signal - three products share this page. The event carries the product key and nothing
            else: no message, no address, no enquiry content. */}
        <a
          href={`mailto:${ENQUIRY_EMAIL}?subject=${encodeURIComponent(svc.enquirySubject)}&body=${encodeURIComponent(svc.enquiryBody)}`}
          onClick={() => recordProductOperation(SECURITY_PRODUCT_KEYS[svc.key], true, 'enquiry_started', 'web')}
          style={{ ...pg.cta, ...glassSurface(svc.accent2, svc.accent, svc.bright) }}
        >
          {svc.ctaLabel}
        </a>

        {/* Opens this product's uniform Dashboard detail page. Not a
            purchase or launch control. */}
        <div style={pg.moreInfoWrap}>
          <MoreInfoPill productKey={svc.key} accent={svc.accent} />
        </div>
      </div>
    </div>
  );
}

// -- Page ---------------------------------------------------------------------

export default function SecurityPage() {
  // Universal product activity. This page IS the launch surface for line_security, so mounting it
  // is the only honest evidence that the customer opened the product. Once per browser
  // session; the payload carries no identity, no timestamp and no customer content, and a
  // failure is swallowed rather than allowed to break the product.
  useEffect(() => { recordProductOpened('line_security', 'web'); }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'sec-motion-css';
    if (!document.getElementById('sec-motion-css')) document.head.appendChild(style);
    style.textContent = SEC_MOTION_CSS;

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('sec-in'); observer.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sec-reveal').forEach(el => observer.observe(el));
    return () => { observer.disconnect(); };
  }, []);

  return (
    <div style={pg.page}>
      <ProductNav />

      {/* ============================ STANDARD VISINVOKE PRODUCT HEADER */}
      <section style={pg.headerSection}>
        <div className="vi-desktop-wide" style={pg.container}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <h1 style={pg.headerTitle}>VISINVOKE Security</h1>
          </div>
          <p style={pg.headerSub}>Professional security services designed for home users, professionals and businesses.</p>
        </div>
      </section>

      {/* =================================================== SERVICE CARDS */}
      <section style={pg.cardsSection}>
        <div className="vi-desktop-wide" style={pg.cardsContainer}>
          <div style={pg.cardGrid} className="sec-card-grid">
            <ServiceCard svc={SERVICES[0]} delay="sec-reveal-delay-1" />
            <ServiceCard svc={SERVICES[1]} delay="sec-reveal-delay-2" />
          </div>

          {/* How VISINVOKE Security Works - full-width 3-column overview */}
          <div className="sec-reveal" style={pg.infoPanel}>
            <h2 style={pg.infoTitle}>How VISINVOKE Security Works</h2>
            <div style={pg.infoGrid} className="sec-info-grid">
              {INFO_COLUMNS.map((c, i) => (
                <div key={i} style={{ ...pg.infoCol, borderColor: c.border, background: c.bg }}>
                  <div style={{ ...pg.infoColLine, background: c.line }} />
                  <div style={{ ...pg.infoIcon, color: c.accentText, background: c.iconBg, borderColor: c.iconBorder }}>{c.icon}</div>
                  <div style={{ ...pg.infoLabel, color: c.accentText }}>{c.label}</div>
                  <p style={pg.infoText}>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// -- Styles -------------------------------------------------------------------

const pg = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(1200px 640px at 50% -4%, rgba(242,185,30,0.09) 0%, rgba(0,0,0,0) 44%), radial-gradient(1000px 720px at 100% 112%, rgba(242,185,30,0.05) 0%, rgba(0,0,0,0) 55%), #080B11',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#C7CFDC',
  },

  container: { maxWidth: 1280, margin: '0 auto', padding: '0 40px' },

  /* Header */
  headerSection: { position: 'relative', padding: '52px 0 14px' },
  headerTitle: { fontSize: 50, fontWeight: 800, color: '#f6f8fb', lineHeight: 1.1, margin: 0, letterSpacing: '-0.8px', textShadow: '0 0 26px rgba(242,185,30,0.18)' },
  headerSub: { fontSize: 17.5, fontWeight: 500, color: '#c3cad6', lineHeight: 1.55, margin: '12px auto 0', textAlign: 'center', maxWidth: 780 },

  /* Cards section - generous spacing, centered, two large cards */
  cardsSection: { padding: '48px 0 110px' },
  cardsContainer: { maxWidth: 1120, margin: '0 auto', padding: '0 40px' },
  cardGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 36 },

  /* Flagship card */
  card: {
    background: 'linear-gradient(180deg, #16181e 0%, #101217 100%)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 16px 50px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHead: { position: 'relative', padding: '32px 34px', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  iconBox: { width: 60, height: 60, borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)' },
  cardTitle: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 28, fontWeight: 700, color: '#f6f8fb', margin: '10px 0 0', lineHeight: 1.05, letterSpacing: '0.2px' },

  cardBody: { padding: '30px 34px 34px', display: 'flex', flexDirection: 'column', flex: 1 },
  purpose: { fontSize: 15.5, color: '#c3cad6', lineHeight: 1.62, margin: '0 0 26px', fontWeight: 400 },

  featGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '13px 22px', marginBottom: 28 },
  feat: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14.5, color: '#d3dae4', fontWeight: 500 },
  featMark: { fontWeight: 800, flexShrink: 0, fontSize: 13 },

  priceBlock: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, padding: '20px 0 24px', borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 24, flexWrap: 'wrap' },
  priceMain: { display: 'flex', alignItems: 'flex-end', gap: 8 },
  priceNum: { fontSize: 38, fontWeight: 800, color: '#f6f8fb', lineHeight: 1, letterSpacing: '-0.02em' },
  pricePer: { fontSize: 13.5, color: '#95a1b3', fontWeight: 500, paddingBottom: 5 },
  priceSetup: { textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 2 },
  setupAmt: { fontSize: 18, fontWeight: 800, color: '#e8e6e0', lineHeight: 1 },
  setupTxt: { fontSize: 12, color: '#95a1b3' },

  moreInfoWrap: { width: '58%', minWidth: 220, alignSelf: 'center', marginTop: 14 },

  cta: { width: '58%', minWidth: 220, alignSelf: 'center', marginTop: 'auto', textAlign: 'center', whiteSpace: 'nowrap', color: '#fff', borderRadius: 12, padding: '15px 26px', fontSize: 15.5, fontWeight: 700, letterSpacing: '0.02em', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.14)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)', transition: 'transform .15s ease, filter .2s ease, box-shadow .2s ease' },

  /* Information panel - brighter blue, centered (matches Utilities panel proportions) */
  infoPanel: {
    marginTop: 40,
    background: 'linear-gradient(180deg, #14171d 0%, #0f1216 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 22,
    padding: '42px 44px 46px',
    boxShadow: '0 16px 50px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 34px rgba(242,185,30,0.06)',
  },
  infoTitle: { fontSize: 30, fontWeight: 800, color: '#f4f7fb', letterSpacing: '-0.01em', lineHeight: 1.15, margin: '0 0 30px', textAlign: 'center', textShadow: '0 0 22px rgba(242,185,30,0.16)' },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22, alignItems: 'stretch' },
  infoCol: { position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 12, padding: '26px 24px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)', boxSizing: 'border-box', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' },
  infoColLine: { position: 'absolute', top: 0, left: 0, right: 0, height: 3 },
  infoIcon: { width: 46, height: 46, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 },
  infoLabel: { fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 },
  infoText: { fontSize: 14.5, color: '#c5ccd8', lineHeight: 1.65, margin: 0, fontWeight: 400 },
};
